import { Box, ButtonDanger, ButtonLight, Text } from 'front-end/src/components'
import React from 'react'
import { useRouteMatch } from 'react-router-dom'

import { t, Trans } from '@lingui/macro'

import { Modal } from '../../../components'
import ErrorMessage from '../../../components/common/error/ErrorMessage'
import Loader from '../../../components/common/loader/Loader'
import { notification } from '../../../components/common/notifications/Notifications'
import { useWorkspaceManageTopicsAndThemesQuery } from '../resources-subpage/workspaceManageTopicsAndThemes.codegen'
import {
  TopicRelatedInstancesFragment,
  useArchiveTopicMutation,
  useTopicRelatedInstancesQuery
} from './DeleteResourceModal.codegen'
import { ResourceRelatedInstances } from './ResourceRelatedInstances'

interface IDeleteResourceModalProps {
  resourceId: number | null
  resolve: () => void
  reject: () => void
}

const DeleteResourceModal = ({
  resourceId,
  resolve,
  reject
}: IDeleteResourceModalProps) => {
  if (!resourceId) {
    return null
  }
  // @ts-expect-error
  const { params } = useRouteMatch<{ workspaceId: string }>(
    '/workspace/:workspaceId'
  )

  const { refetch } = useWorkspaceManageTopicsAndThemesQuery({
    variables: {
      workspaceId: params.workspaceId
    }
  })
  const { data, loading } = useTopicRelatedInstancesQuery({
    variables: { topicId: resourceId }
  })

  const [archiveTopic] = useArchiveTopicMutation({
    variables: { topicId: resourceId }
  })

  if (!data || loading) {
    return <Loader />
  }

  const topic = data.topic as TopicRelatedInstancesFragment

  if (!data.topic) {
    return (
      <ErrorMessage>
        <Trans>
          The resource {resourceId} does not exist or you don't have access to
          it
        </Trans>
      </ErrorMessage>
    )
  }

  const relatedBlockingInstancesCount =
    topic.relatedCertifications.length +
    topic.relatedCampaignsByActions.length +
    topic.relatedCampaignsByFilters.length

  const relatedInstancesCount =
    relatedBlockingInstancesCount + topic.relatedPathways.length

  if (relatedBlockingInstancesCount) {
    return (
      <Modal
        newDesign
        header={t`Unable to archive resource`}
        controls={
          <>
            <ButtonLight onClick={reject}>
              <Trans>Cancel</Trans>
            </ButtonLight>
          </>
        }
        onHide={reject}
      >
        <Box mb="16px">
          <Text>
            <Trans>
              This resource cannot be archived as it appears in the following
              instances.
            </Trans>
          </Text>
          <Text>
            <Trans>
              Should you still wish to archive this resource, please update the
              relevant item or contact their owner.
            </Trans>
          </Text>
        </Box>

        <ResourceRelatedInstances topic={topic} />
      </Modal>
    )
  }

  return (
    <Modal
      newDesign
      header={t`Are you sure you want to archive this resource?`}
      controls={
        <>
          <ButtonLight onClick={reject}>
            <Trans>Cancel</Trans>
          </ButtonLight>
          <ButtonDanger
            onClickAsync={async () => {
              try {
                await archiveTopic()
                refetch()
                resolve()
                notification.info(t`Resource successfully archived`)
              } catch (e) {
                notification.error(
                  t`Error while archiving resource, please try again later.`
                )
              }
            }}
            dataTest="ArchiveResourceModalButton"
          >
            <Trans>Archive resource</Trans>
          </ButtonDanger>
        </>
      }
      onHide={reject}
    >
      <Text>
        <Trans>
          Access to this resource will be removed for{' '}
          <strong>everyone. This action cannot be undone</strong>.
        </Trans>
      </Text>

      {relatedInstancesCount > 0 && (
        <Text pb="8px">
          <Trans>This resource appears in the following instances.</Trans>
        </Text>
      )}

      <ResourceRelatedInstances topic={topic} />
    </Modal>
  )
}

export default DeleteResourceModal
